'use client'

import { useState } from 'react'
import { poster } from './ProductionShared'

// A web, not a chain: references fan into takes, takes branch and merge into the final.
// Nodes mix asset types — stills, video (play + duration) and audio (waveform) — because
// provenance in Martini crosses media. `crop` picks which third of a wide still shows.

type Kind = 'image' | 'video' | 'audio'
type Node = { id: string; x: number; y: number; kind: Kind; label: string; src?: string; crop?: string; dur?: string }

const NODES: Node[] = [
  { id: 'r1', x: 20, y: 70, kind: 'image', src: poster(29), crop: 'xMidYMid', label: 'Character ref' },
  { id: 'r2', x: 20, y: 210, kind: 'image', src: poster(30), crop: 'xMidYMid', label: 'Location ref' },
  { id: 'r3', x: 20, y: 350, kind: 'audio', label: 'VO take 2', dur: '0:12' },
  { id: 't1', x: 270, y: 120, kind: 'video', src: poster(22), crop: 'xMidYMid', label: 'Take 1', dur: '0:05' },
  { id: 't2', x: 270, y: 300, kind: 'video', src: poster(6), crop: 'xMinYMid', label: 'Take 2', dur: '0:05' },
  { id: 't3', x: 520, y: 70, kind: 'video', src: poster(22), crop: 'xMaxYMid', label: 'Extend', dur: '0:10' },
  { id: 't4', x: 520, y: 210, kind: 'image', src: poster(22), crop: 'xMinYMid', label: 'Keyframe' },
  { id: 't5', x: 520, y: 350, kind: 'video', src: poster(6), crop: 'xMaxYMid', label: 'Lip sync', dur: '0:05' },
  { id: 'f', x: 770, y: 210, kind: 'video', src: poster(22), crop: 'xMidYMid', label: 'Final', dur: '0:12' },
]
const EDGES: [string, string][] = [
  ['r1', 't1'],
  ['r2', 't1'],
  ['r1', 't2'],
  ['r3', 't2'],
  ['t1', 't3'],
  ['t1', 't4'],
  ['t2', 't5'],
  ['r3', 't5'],
  ['t3', 'f'],
  ['t4', 'f'],
  ['t5', 'f'],
]
const NODE_W = 150
const NODE_H = 84
const WAVE = Array.from({ length: 34 }, (_, i) =>
  Number((0.2 + 0.8 * Math.abs(Math.sin(i * 1.3) * Math.cos(i * 0.4))).toFixed(3)),
)

// Everything upstream and downstream of a node: what it came from and what it made.
function lineageOf(id: string): Set<string> {
  const seen = new Set([id])
  const walk = (from: string, dir: 0 | 1) => {
    for (const edge of EDGES) {
      const next = edge[dir === 1 ? 1 : 0]
      if (edge[dir === 1 ? 0 : 1] === from && !seen.has(next)) {
        seen.add(next)
        walk(next, dir)
      }
    }
  }
  walk(id, 0)
  walk(id, 1)
  return seen
}

function Badge({ n }: { n: Node }) {
  const x = n.x + 6
  const y = n.y + NODE_H / 2 - 6
  if (n.kind === 'video') {
    return (
      <g>
        <rect x={x} y={y - 16} width={44} height={16} rx="3" fill="rgba(0,0,0,0.65)" />
        <polygon points={`${x + 6},${y - 12} ${x + 6},${y - 4} ${x + 13},${y - 8}`} fill="#fff" />
        <text x={x + 17} y={y - 4.5} fontSize="10" fontFamily="ui-monospace, monospace" fill="#fff">
          {n.dur}
        </text>
      </g>
    )
  }
  if (n.kind === 'audio') {
    return (
      <text x={x} y={y - 4.5} fontSize="10" fontFamily="ui-monospace, monospace" fill="rgba(255,255,255,0.7)">
        WAV · {n.dur}
      </text>
    )
  }
  return null
}

export function ProductionLineage() {
  const [hovered, setHovered] = useState<string | null>(null)
  const lit = hovered ? lineageOf(hovered) : null
  const at = (id: string) => NODES.find(n => n.id === id)!

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox="0 0 940 440"
        className="h-auto w-full min-w-[720px]"
        role="img"
        aria-label="Lineage graph of references, takes and the final shot"
        onMouseLeave={() => setHovered(null)}
      >
        <defs>
          {NODES.map(n => (
            <clipPath key={n.id} id={`lineage-${n.id}`}>
              <rect x={n.x} y={n.y - NODE_H / 2} width={NODE_W} height={NODE_H} rx="8" />
            </clipPath>
          ))}
        </defs>
        {/* Column captions */}
        {[
          ['References', 20],
          ['Takes', 270],
          ['Iterations', 520],
          ['Final', 770],
        ].map(([label, x]) => (
          <text key={label} x={x} y={14} fontSize="11" letterSpacing="0.08em" fill="rgba(255,255,255,0.3)">
            {String(label).toUpperCase()}
          </text>
        ))}
        {EDGES.map(([a, b]) => {
          const from = at(a)
          const to = at(b)
          const x1 = from.x + NODE_W
          const mid = (x1 + to.x) / 2
          const on = !lit || (lit.has(a) && lit.has(b))
          return (
            <path
              key={`${a}-${b}`}
              d={`M${x1} ${from.y} C ${mid} ${from.y}, ${mid} ${to.y}, ${to.x} ${to.y}`}
              fill="none"
              stroke={lit && on ? '#fff' : 'rgba(255,255,255,0.28)'}
              strokeOpacity={on ? 1 : 0.2}
              strokeWidth={lit && on ? 1.8 : 1.2}
              className="transition-all duration-200"
            />
          )
        })}
        {NODES.map(n => {
          const on = !lit || lit.has(n.id)
          const active = hovered === n.id || (!hovered && n.id === 'f')
          const top = n.y - NODE_H / 2
          return (
            <g
              key={n.id}
              onMouseEnter={() => setHovered(n.id)}
              className="cursor-pointer transition-opacity duration-200"
              opacity={on ? 1 : 0.25}
            >
              {n.kind === 'audio' ? (
                <g clipPath={`url(#lineage-${n.id})`}>
                  <rect x={n.x} y={top} width={NODE_W} height={NODE_H} fill="#1c2a22" />
                  {WAVE.map((h, i) => (
                    <rect
                      key={i}
                      x={(n.x + 10 + i * 3.9).toFixed(1)}
                      y={(n.y - h * 22).toFixed(2)}
                      width={2}
                      height={(h * 44).toFixed(2)}
                      rx="1"
                      fill="rgba(134, 239, 172, 0.7)"
                    />
                  ))}
                </g>
              ) : (
                <image
                  href={n.src}
                  x={n.x}
                  y={top}
                  width={NODE_W}
                  height={NODE_H}
                  preserveAspectRatio={`${n.crop} slice`}
                  clipPath={`url(#lineage-${n.id})`}
                />
              )}
              <Badge n={n} />
              <rect
                x={n.x}
                y={top}
                width={NODE_W}
                height={NODE_H}
                rx="8"
                fill="none"
                stroke={active ? '#fff' : 'rgba(255,255,255,0.18)'}
                strokeWidth={active ? 2 : 1}
              />
              <text x={n.x} y={n.y + NODE_H / 2 + 20} fontSize="14" fill="rgba(255,255,255,0.65)">
                {n.label}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
